import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDPagination from 'components/MDPagination';
import Icon from '@mui/material/Icon';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { apiGetAllPurchases } from '../../api/gift.api';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { handleDownloadExcel } from '../../components/excel/excelUtils';
import MDButton from '../../components/MDButton';
import { Snackbar, Alert } from '@mui/material';

const Gift = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { user, appIsReady } = useData();

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setCreatedFrom(today);
    setCreatedTo(today);
  }, []);

  useEffect(() => {
    if (createdFrom && createdTo && appIsReady && user) {
      fetchPurchases(page);
    }
  }, [page, user, appIsReady, createdFrom, createdTo]);

  const fetchPurchases = async (pageNum) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pageNum,
        limit: limit
      };
      
      const response = await apiGetAllPurchases(queryParams);
      if (response && response.data) {
        console.log("API 응답 데이터:", response.data);
        
        const purchasesData = response.data.data || [];
        setPurchases(purchasesData);
        
        const metaData = response.data.meta || {};
        setTotal(metaData.total || 0);
        
        console.log("구매 내역 데이터:", purchasesData);
        console.log("총 개수:", metaData.total);
      } else {
        console.log('데이터가 수신되지 않았습니다');
        setPurchases([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('구매 내역 로드 실패:', error);
      setPurchases([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
  };

  const handleSearch = () => {
    fetchPurchases(1);
    setPage(1);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit', 
      hour12: false 
    };
    return new Date(dateString).toLocaleString('ko-KR', options).replace(/. /g, '-').replace('.', '').replace(' ', ' ');
  };

  const rows = purchases.map(purchase => ({
    id: purchase.id,
    userId: purchase.user_id || '',
    userName: purchase.user?.name || '',
    userEmail: purchase.user?.email || '',
    couponCode: purchase.coupon_code || '',
    couponNumber: purchase.coupon_number || '',
    pinNumber: purchase.pin_number || '',
    productName: purchase.product_name || '',
    recipientPhone: purchase.recipient_phone || '',
    pointsUsed: purchase.points_used || 0,
    message: purchase.message || '',
    createdAt: purchase.created_at ? formatDate(purchase.created_at) : '-',
  }));

  const columns = [
    { Header: "ID", accessor: "id", align: "left" },
    { Header: "사용자 ID", accessor: "userId", align: "left" },
    { Header: "사용자 이름", accessor: "userName", align: "left" },
    { Header: "사용자 이메일", accessor: "userEmail", align: "left" },
    { Header: "쿠폰코드", accessor: "couponCode", align: "left" },
    { Header: "쿠폰번호", accessor: "couponNumber", align: "left" },
    { Header: "핀번호", accessor: "pinNumber", align: "left" },
    { Header: "상품명", accessor: "productName", align: "left" },
    { Header: "수신자번호", accessor: "recipientPhone", align: "left" },
    { Header: "사용포인트", accessor: "pointsUsed", align: "right" },
    { Header: "메시지", accessor: "message", align: "left" },
    { Header: "생성일시", accessor: "createdAt", align: "center" },
  ];

  const totalPages = Math.ceil(total / limit);
  const maxPageNumbers = 10;
  const currentPageGroup = Math.floor((page - 1) / maxPageNumbers);
  const startPage = currentPageGroup * maxPageNumbers + 1;
  const endPage = Math.min(startPage + maxPageNumbers - 1, totalPages);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);

  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton variant="gradient" color="info" onClick={() => handleDownloadExcel('기프티콘발급내역', columns, rows)} sx={{ ml: 2 }}>엑셀 다운로드</MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  기프티콘 구매/발급 내역
                </MDTypography>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={3}
              >
                <MDPagination
                  variant="gradient"
                  color="info"
                >
                  {page > 1 && (
                    <MDPagination item onClick={() => handlePageChange(page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {pageNumbers.map(pageNum => (
                    <MDPagination
                      item
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      active={page === pageNum}
                    >
                      {pageNum}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination item onClick={() => handlePageChange(page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Gift;